/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


/*
 * GPS tracking API client. Talks to the reused aegis-ao-toll-tracker service
 * (run against the aegis-aa SQL Server DB) via the owner-web proxy under
 * /api/toll-tracker/* — server.js rewrites that prefix to the toll-tracker API's
 * /api/* and forwards Authorization + X-Owner-Id (owner scoping). Responses are
 * raw JSON (not the {result} envelope used by the main API).
 */
import apiClient from './client';

const BASE = '/toll-tracker';

export interface Trip {
  id: string;
  vehicleId: string;
  startedAt: string;
  endedAt: string;
  distanceMeters: number;
  durationSeconds: number;
}

export interface TripTrack {
  id: string;
  vehicleId: string;
  startedAt: string;
  endedAt: string;
  distanceMeters: number;
  durationSeconds: number;
  // [lon, lat, secondsSinceStart][]
  points: number[][];
}

export interface LastPosition {
  vehicleId: string;
  trackerId: string;
  ts: string;
  lon: number;
  lat: number;
  speedKmh: number;
  angle: number;
  ignition: boolean | null;
}

export interface VehicleTracker {
  trackerId: string;
  vehicleId: string;
  imei: string;
  model: string;
  lastSeenAt: string | null;
  isActive: boolean;
  /** Navigator origin: null = our own (hardware) device; otherwise the GPS plugin id (e.g. "fleet77"). */
  source: string | null;
}

export interface TollHit {
  boothId: number;
  provider: string | null;
  name: string | null;
  ref: string | null;
  lat: number;
  lon: number;
  hitAt: string;
  distanceMeters: number;
  // True for managed/express/HOT-lane gantries that run in the median of a free highway. The car may
  // have been on the adjacent free lanes, so this is a "possible" toll, not a confirmed passage.
  uncertain?: boolean;
  // Max 2-axle worst-case toll for this booth (public rate schedules), pay-by-plate/tolls-by-mail
  // channel. Null when unknown or when the facility is dynamically priced with no published cap.
  maxTollAmount?: number | null;
  // Pricing model: Flat | Distance | DynamicCap | Uncapped | NonHighway | NeedsReview.
  maxTollType?: string | null;
  // 'booth' | 'ticket' | 'zone' | 'parking'. 'parking' = the car dwelled in a parking zone (not a road toll).
  kind?: string | null;
}

export interface TripFull extends TripTrack {
  tolls: TollHit[];
}

/**
 * A pre-computed, priced GPS toll for a booking (from dbo.GpsTolls, populated after the booking ends).
 * Entry×exit-aware for ticketed turnpikes (name = "Entry → Exit"). These are the early GPS estimate,
 * shown until the authoritative agency tolls post ~2–4 days later.
 */
export interface GpsToll {
  id: number;
  bookingId: string | null;
  tollAtUtc: string;
  kind: string;                 // 'booth' | 'ticket' | 'zone'
  provider: string | null;
  name: string | null;          // booth name or "Entry → Exit"
  entryCode: string | null;
  exitCode: string | null;
  facilityCode: string | null;  // ticket-road facility (e.g. NJ_TURNPIKE); null for booth/zone
  lat: number | null;
  lon: number | null;
  amount: number | null;        // worst-case (pay-by-plate) price
  uncertain: boolean;
}

export interface BookingTrack {
  bookingId: string;
  vehicleId: string;
  periodStart: string;
  periodEnd: string;
  // [lon, lat, secondsSinceStart, speedKmh?, ignition?][]
  points: number[][];
  tolls: TollHit[];
}

export interface VehicleTrack {
  vehicleId: string;
  from: string;
  to: string;
  // [lon, lat, secondsSinceStart, speedKmh?, ignition?][]
  points: number[][];
  tolls: TollHit[];
}

export interface FleetVehiclePosition {
  vehicleId: string;
  trackerId: string;
  imei: string;
  licensePlate: string | null;
  make: string | null;
  modelName: string | null;
  year: number | null;
  ts: string;
  lon: number;
  lat: number;
  speedKmh: number;
  angle: number;
  ignition: boolean | null;
  /** Navigator origin: null = our own (hardware) device; otherwise the GPS plugin id (e.g. "fleet77"). */
  source: string | null;
}

export interface TollBooth {
  id: number;
  provider: string | null;
  name: string | null;
  ref: string | null;
  lat: number;
  lon: number;
  headingDeg: number | null;
}

async function get<T>(path: string): Promise<T> {
  const res = await apiClient.get<T>(`${BASE}${path}`);
  return res.data;
}

async function post<T>(path: string): Promise<T> {
  const res = await apiClient.post<T>(`${BASE}${path}`, undefined, { timeout: 120000 });
  return res.data;
}

/** Result of persisting a car's tolls for an arbitrary period (booking-less). */
export interface PeriodTollsResult {
  vehicleId: string;
  from: string;
  to: string;
  count: number;
  tolls: TollHit[];
}

export const gpsApi = {
  trackers: () => get<VehicleTracker[]>(`/trackers`),
  trips: (vehicleId: string, from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const q = qs.toString();
    return get<Trip[]>(`/vehicles/${vehicleId}/trips${q ? `?${q}` : ''}`);
  },
  track: (id: string) => get<TripTrack>(`/trips/${id}/track`),
  tripFull: (id: string) => get<TripFull>(`/trips/${id}/full`),
  // Full GPS track for a booking's rental window (vehicle + period resolved
  // server-side from the bookingId), with on-demand toll matching. Mirrors the
  // aegis-rental `bookingTrack` endpoint.
  bookingTrack: (bookingId: string) => get<BookingTrack>(`/bookings/${bookingId}/track`),
  // Pre-computed priced GPS tolls for a booking (fast; from dbo.GpsTolls, populated after the booking ends).
  bookingGpsTolls: (bookingId: string) => get<GpsToll[]>(`/bookings/${bookingId}/gps-tolls`),
  // Agency-reconciliation fallback: the O/D fare for a ticketed turnpike by CODES (facility/entry/exit).
  // Used when the GPS track can't pin the interchanges but the agency posted its own entry/exit.
  odFare: (facility: string, entry: string, exit: string) =>
    get<{ worstCase: number | null; plate: number | null; transponder: number | null; cash: number | null }>(
      `/od-fare?facility=${encodeURIComponent(facility)}&entry=${encodeURIComponent(entry)}&exit=${encodeURIComponent(exit)}`),
  // Full GPS track for a vehicle over an arbitrary [from, to] window (ISO), with
  // on-demand toll matching. Used for the "Trip by dates" mode.
  vehicleTrack: (vehicleId: string, from: string, to: string) => {
    const qs = new URLSearchParams({ from, to });
    return get<VehicleTrack>(`/vehicles/${vehicleId}/track?${qs}`);
  },
  // Compute + PERSIST priced GPS tolls for a car over an arbitrary [from, to] window,
  // independent of any booking (written to dbo.GpsTolls with BookingId = NULL). Idempotent.
  computePeriodTolls: (vehicleId: string, from: string, to: string) => {
    const qs = new URLSearchParams({ from, to });
    return post<PeriodTollsResult>(`/vehicles/${vehicleId}/compute-period-tolls?${qs}`);
  },
  last: (vehicleId: string) => get<LastPosition>(`/vehicles/${vehicleId}/last-position`),
  fleetPositions: () => get<FleetVehiclePosition[]>(`/fleet/positions`),
  tollBooths: (minLat: number, maxLat: number, minLon: number, maxLon: number, provider?: string) => {
    const qs = new URLSearchParams({
      minLat: String(minLat), maxLat: String(maxLat),
      minLon: String(minLon), maxLon: String(maxLon),
    });
    if (provider) qs.set('provider', provider);
    return get<TollBooth[]>(`/toll-booths?${qs}`);
  },
};
